import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';

import { ApiFormService, Validators } from '@personal-area/global/form-validation/services';
import { ImageServerFile } from '@personal-area/global/common';
import {
  ButtonComponent,
  ActionStatusModule,
  CardWrapperModule,
  FormFieldComponent,
  FILE_TYPE,
  FileAttachModule,
  FileAttachSettings
} from '@personal-area/global/ui';
import { MIME_TYPES } from '@personal-area/global/http/models';
import { UserFacade } from '@personal-area/global/users/services';
import { TranslateModule } from '@ngx-translate/core';
import {
  BehaviorSubject,
  combineLatest,
  finalize,
  first,
  map,
  switchMap,
} from 'rxjs';

import { CardVerificationApiService } from '../../services/card-verification-api.service';
import { CardVerificationFacade } from '../../state/facades/card';
import { BreadcrumbsComponent } from '@personal-area/global/navigation-layout';

@Component({
  templateUrl: './card-verification.component.html',
  styleUrls: ['./card-verification.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ActionStatusModule,
    FileAttachModule,
    ReactiveFormsModule,
    FormFieldComponent,
    BreadcrumbsComponent,
    CardWrapperModule,
    ButtonComponent,
  ],
})
export class CardVerificationComponent {
  public readonly fileAttachSettings: FileAttachSettings = {
    allowedContentTypes: [
      MIME_TYPES.jpg,
      MIME_TYPES.png,
      MIME_TYPES.bmp,
      MIME_TYPES.pdf,
    ],
    allowedFileSize: 5242880, // 5mb
    maxFiles: 2,
  };
  public readonly allowedFileTypes = this.fileAttachSettings.allowedContentTypes
    .map((type) => FILE_TYPE[type])
    .join(', ');
  public readonly fieldErrors = {
    availableChars: 'common.error.chars-are-not-available',
    required: `profile-card-verification.error.files-required`,
  };
  public submitErrors = new EventEmitter<void>();
  public verificationForm = this._formBuilder.nonNullable.group({
    files: [[] as ImageServerFile[], Validators.required],
  });
  public cardNumber$ = this._cardVerificationFacade.card$.pipe(
    map(({ card }) => card),
  );
  public isSent$ = new BehaviorSubject(false);
  public isLoading$ = new BehaviorSubject(false);

  constructor(
    private _userFacade: UserFacade,
    private _formService: ApiFormService,
    private _cardVerificationService: CardVerificationApiService,
    private _cardVerificationFacade: CardVerificationFacade,
    private _formBuilder: FormBuilder,
  ) {}

  public formSubmit(): void {
    if (!this.verificationForm.valid) {
      this.submitErrors.next();
      return;
    }

    this.isLoading$.next(true);
    combineLatest([this._userFacade.user$, this.cardNumber$])
      .pipe(
        first(),
        switchMap(([{ id }, cardNumber]) =>
          this._cardVerificationService.request(id, {
            cardNumber: cardNumber,
            files: this.verificationForm.value.files || [],
          }),
        ),
        finalize(() => this.isLoading$.next(false)),
      )
      .subscribe({
        next: () => {
          this.isSent$.next(true);
          this._cardVerificationFacade.getCard();
        },
        error: (err: HttpErrorResponse) => {
          this._formService.defaultFormError(err, this.verificationForm);
          this.submitErrors.next();
        },
      });
  }
}
